import React, { forwardRef } from "react"
import styled from "@emotion/styled"
import { variant } from "styled-system"
import Box, { BoxProps } from "./Box"
import { useTranslation } from "react-i18next"
import { getWordBreak } from "./Text"

export interface TypographyProps extends BoxProps {
  whiteSpace?: string
  leadingTrim?: boolean
  as?: string
  variant: string
  wordBreak?: string
}

const getLeadingTrimStyle = fontSizes => ({
  mt: fontSizes.map(fontSize => `-${fontSize * 0.25}px`),
  transform: fontSizes.map(fontSize => `translateY(${fontSize * 0.25}px)`),
})

const Typography = styled(Box)<TypographyProps>`
  word-break: ${({ wordBreak }) => wordBreak || "normal"};
  white-space: ${({ whiteSpace }) => whiteSpace || "pre-wrap"};
  ${({ leadingTrim = false }) =>
    variant({
      variants: {
        h1: {
          fontFamily: "heading",
          fontSize: [40, 40, 64],
          lineHeight: ["120%"],
          letterSpacing: ["-0.01em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([40, 40, 64])),
        },
        h1NoDefi: {
          // used in no defi page header text
          fontFamily: "heading",
          fontSize: [36, 48, 74],
          lineHeight: ["115%", "125%", "115%"],
          letterSpacing: ["-0.015em", "-0.65px", "-0.015em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([48, 48, 74])),
        },
        h1Partners: {
          // only using in Partners Page PartnersHead main text
          fontFamily: "heading",
          fontSize: [32, 48, 74],
          lineHeight: ["115%", "125%", "115%"],
          letterSpacing: ["-0.015em", "-0.65px", "-0.015em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([48, 48, 74])),
        },
        h2: {
          fontFamily: "heading",
          fontSize: [32, 32, 48],
          lineHeight: ["125%"],
          letterSpacing: ["-0.015em", "-0.25px", "-0.015em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([32, 32, 48])),
        },
        h3: {
          fontFamily: "heading",
          fontSize: [32, 25, 48],
          lineHeight: ["125%", "132%", "125%"],
          letterSpacing: ["-0.015em", "0px", "-0.015em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([32, 25, 48])),
        },
        newH3: {
          fontFamily: "heading",
          fontSize: [24, 28, 28],
          lineHeight: ["140%"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([24, 28, 28])),
        },
        h4: {
          fontFamily: "heading",
          fontSize: [14, 15, 18],
          lineHeight: ["125%", "145%", "115%"],
          letterSpacing: ["0.03em", "0.4px", "0.03em"],
          fontWeight: ["bold", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([14, 15, 18])),
        },
        h5: {
          fontFamily: "heading",
          fontSize: [22, 21, 32],
          lineHeight: ["125%", "130%", "125%"],
          letterSpacing: ["0em", "0.1px", "0em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([22, 21, 32])),
        },
        h6: {
          fontFamily: "heading",
          fontSize: [18, 18, 18],
          lineHeight: ["120%", "130%", "125%"],
          letterSpacing: ["0em", "0px", "0em"],
          fontWeight: ["500", "500", "500"],
          ...(leadingTrim && getLeadingTrimStyle([18, 18, 18])),
        },
        h7: {
          fontFamily: "heading",
          fontSize: [15, 15, 15],
          lineHeight: ["125%", "145%", "125%"],
          letterSpacing: ["0.03em", "0.5px", "0.03em"],
          fontWeight: ["bold", "bold", "bold"],
          ...(leadingTrim && getLeadingTrimStyle([15, 15, 15])),
        },
        subtitle: {
          fontFamily: "heading",
          fontSize: [17, 16, 24],
          lineHeight: ["130%", "130%", "145%"],
          letterSpacing: ["0em", "0.1px", "0em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([17, 16, 24])),
        },
        subtitle_uk: {
          fontFamily: "heading",
          fontSize: [17, 16, 20],
          lineHeight: ["130%", "130%", "145%"],
          letterSpacing: ["0em", "0.1px", "0em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([17, 16, 24])),
        },
        subtitle2: {
          fontFamily: "heading",
          fontSize: [16, 17, 18],
          lineHeight: ["135%", "135%", "145%"],
          letterSpacing: ["-0.1px", "0.01em", "0em"],
          fontWeight: ["500", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([16, 17, 18])),
        },
        subtitle6: {
          fontFamily: "heading",
          fontSize: [14, 14, 14],
          lineHeight: ["125%", "145%", "170%"],
          letterSpacing: ["0.03em", "0.4px", "0.01em"],
          fontWeight: ["700", "700", "700"],
          ...(leadingTrim && getLeadingTrimStyle([14, 14, 14])),
        },
        subtitle4: {
          fontFamily: "heading",
          fontSize: [14, 14, 14],
          lineHeight: ["125%", "145%", "170%"],
          letterSpacing: ["0.03em", "0.4px", "0.01em"],
          fontWeight: ["500", "500", "500"],
          ...(leadingTrim && getLeadingTrimStyle([14, 14, 14])),
        },
        menuTitle: {
          fontFamily: "heading",
          fontSize: ["18px"],
          lineHeight: ["22px", "22px", "22px"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([18])),
        },
        menuSubTitle: {
          fontFamily: "heading",
          fontSize: ["18px"],
          lineHeight: ["22px", "22px", "22px"],
          ...(leadingTrim && getLeadingTrimStyle([18])),
        },
        langTitle: {
          fontFamily: "heading",
          fontSize: ["22px"],
          lineHeight: ["22px", "22px", "22px"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([18])),
        },
        submenuTitle: {
          fontFamily: "heading",
          fontSize: ["15px"],
          lineHeight: ["20px", "20px", "20px"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([18])),
        },
        subtitle5: {
          fontFamily: "heading",
          fontSize: ["16px"],
          lineHeight: ["16px", "16px", "16px"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([16])),
        },
        subtitle7: {
          fontFamily: "heading",
          fontSize: [25, 25, 32],
          lineHeight: ["135%", "135%", "145%"],
          letterSpacing: ["-0.1px", "0.01em", "0em"],
          fontWeight: ["500", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([27, 25, 32])),
        },
        hiddenBodyCopy: {
          visibility: "hidden",
          lineHeight: ["0%"],
          marginBottom: 0,
          height: 0,
        },
        bodyCopy: {
          fontFamily: "body",
          fontSize: [16, 14, 20],
          lineHeight: ["135%", "135%", "145%"],
          letterSpacing: ["0em", "0.15px", "0em"],
          fontWeight: ["400"],
          ...(leadingTrim && getLeadingTrimStyle([16, 14, 20])),
        },
        bodyCopy2: {
          fontFamily: "body",
          fontSize: [16, 16, 18],
          lineHeight: ["140%", "140%", "140%"],
          letterSpacing: "0em",
          fontWeight: ["500"],
          ...(leadingTrim && getLeadingTrimStyle([16, 14, 20])),
        },
        textCta: {
          fontFamily: "heading",
          fontSize: [16, 14, 18],
          lineHeight: ["145%", "145%", "100%"],
          letterSpacing: ["0em", "0.4px", "0.02em"],
          fontWeight: ["600", "500", "bold"],
          ...(leadingTrim && getLeadingTrimStyle([16, 14, 18])),
        },
        textCta2: {
          fontFamily: "heading",
          fontSize: [12, 12, 14],
          lineHeight: ["132%", "145%", "145%"],
          letterSpacing: ["0.015em", "0.2px", "0.01em"],
          fontWeight: ["500", "600", "500"],
          ...(leadingTrim && getLeadingTrimStyle([12, 12, 14])),
        },
        // for the smallest-size(10px) subtitle, used in earn page, <calculationResult>.
        textSubtitle: {
          fontFamily: "heading",
          fontSize: [14],
          lineHeight: ["145%"],
          letterSpacing: ["1%"],
          fontWeight: ["500"],
          ...(leadingTrim && getLeadingTrimStyle([14])),
        },
        l2: {
          fontFamily: "inherit",
          fontSize: ["16px", "16px", "16px"],
          lineHeight: ["110%", "110%", "110%"],
          fontWeight: ["600", "600", "600"],
        },
        primaryCta: {
          fontFamily: "heading",
          fontSize: [17, 16, 20],
          lineHeight: ["110%", "135%", "100%"],
          letterSpacing: ["0.34px"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([17, 16, 20])),
        },
        secondaryCta: {
          fontFamily: "heading",
          fontSize: [16, 12, 16],
          lineHeight: ["100%", "100%", "100%"],
          letterSpacing: ["0.015em", "0.25px", "0.015em"],
          fontWeight: ["500", "500", "600"],
          ...(leadingTrim && getLeadingTrimStyle([16, 12, 16])),
        },
        overline: {
          fontFamily: "body",
          fontSize: [13, 10, 15],
          lineHeight: ["140%", "145%", "132%"],
          letterSpacing: ["0em", "0.15px", "0.015em"],
          fontWeight: ["normal", "normal", "normal"],
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        // used in fftb page and its verify page
        publicLink: {
          fontFamily: "body",
          fontSize: ["40px", "48px", "72px"],
          color: "#fff",
          lineHeight: ["125%", "125%", "115%"],
          letterSpacing: ["0em", "0.15px", "0.015em"],
          fontWeight: ["bold", "bold", "bold"],
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        mailDisclaimer: {
          fontFamily: "body",
          fontSize: ["8px", "12px", "12px"],
          color: "#7b849b",
          lineHeight: "145%",
          letterSpacing: ["0em", "0.15px", "0.015em"],
          fontWeight: ["normal", "normal", "normal"],
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        tooltip: {
          fontFamily: "body",
          fontSize: "12px",
          color: "#4C4C4C",
          lineHeight: "145%",
          letterSpacing: ["0em", "0.15px", "0.015em"],
          fontWeight: "bold",
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        submitBtn: {
          fontFamily: "body",
          fontSize: "16px",
          color: "#fff",
          letterSpacing: ["0.015em", "0.15px", "0.015em"],
          fontWeight: "600",
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        downloadBtn: {
          fontFamily: "body",
          fontSize: "16px",
          letterSpacing: ["0.015em", "0.15px", "0.015em"],
          fontWeight: "600",
          ...(leadingTrim && getLeadingTrimStyle([16, 16, 16])),
        },
        tooltipDes: {
          fontFamily: "body",
          fontSize: "12px",
          color: "#4C4C4C",
          lineHeight: "145%",
          letterSpacing: ["0em", "0.15px", "0.015em"],
          fontWeight: "normal",
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        link: {
          fontFamily: "body",
          fontSize: [18, 24, 24],
          color: "#fff",
          lineHeight: "115%",
          letterSpacing: ["0em", "0.15px", "0.015em"],
          fontWeight: "normal",
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        highlightTitle: {
          fontFamily: "body",
          fontSize: [32, 48, 60],
          display: "inline",
          lineHeight: "115%",
          letterSpacing: ["0em", "0.15px", "0.015em"],
          fontWeight: ["bold"],
          ...(leadingTrim && getLeadingTrimStyle([13, 10, 15])),
        },
        caption: {
          fontFamily: "body",
          fontSize: [14, 14, 14],
          lineHeight: ["132%", "132%", "150%"],
          letterSpacing: ["0.015em", "0px", "0em"],
          fontWeight: ["500", "500", "500"],
          ...(leadingTrim && getLeadingTrimStyle([14, 14, 14])),
        },
        menuDescription: {
          fontFamily: "body",
          fontSize: [14, 14, 14],
          lineHeight: ["150%", "150%", "150%"],
          fontWeight: ["500", "500", "500"],
          ...(leadingTrim && getLeadingTrimStyle([14, 14, 14])),
        },
        badge: {
          fontFamily: "body",
          fontSize: [10],
          lineHeight: ["120%"],
          letterSpacing: ["0.03em"],
          fontWeight: ["700", "700", "700"],
          ...(leadingTrim && getLeadingTrimStyle([10])),
        },
        statsTitle: {
          fontFamily: "body",
          fontSize: [14, 16, 16],
          lineHeight: ["135%"],
          letterSpacing: ["0.00em"],
          fontWeight: ["normal"],
          maxWidth: "fit-content",
          margin: "auto",
          ...(leadingTrim && getLeadingTrimStyle([10])),
        },
        loadCrewText: {
          fontFamily: "body",
          fontSize: [14, 18, 20],
          lineHeight: ["135%"],
          letterSpacing: ["0.00em"],
          fontWeight: ["normal"],
          ...(leadingTrim && getLeadingTrimStyle([10])),
        },
        h1WithImg: {
          fontFamily: "heading",
          fontSize: [32, 42, 48],
          lineHeight: ["115%", "125%", "115%"],
          letterSpacing: ["0em", "0em", "0em"],
          fontWeight: ["600", "600", "600"],
          ...(leadingTrim && getLeadingTrimStyle([32, 42, 48])),
        },
        disclaimer: {
          fontFamily: "body",
          fontSize: [12],
          lineHeight: ["14.4px"],
          letterSpacing: ["0em"],
          fontWeight: ["400"],
          ...(leadingTrim && getLeadingTrimStyle([12])),
        },
        partnerListSubtitle: {
          fontFamily: "heading",
          fontSize: [16, 14, 20],
          lineHeight: ["150%"],
          letterSpacing: ["0.15px"],
          fontWeight: ["100"],
          ...(leadingTrim && getLeadingTrimStyle([16, 14, 20])),
        },
        partnerListCta: {
          fontFamily: "heading",
          fontSize: [16],
          lineHeight: ["100%"],
          letterSpacing: ["1.5%", "0.25px", "1.5%"],
          fontWeight: ["400", "200", "600"],
          ...(leadingTrim && getLeadingTrimStyle([16])),
        },
        earnSelectOptionText: {
          fontFamily: "body",
          fontSize: [20, 19, 20],
          lineHeight: ["110%"],
          letterSpacing: ["0.02em"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([20, 19, 20])),
        },
        earnSelectOptionTitle: {
          fontFamily: "heading",
          fontSize: [24],
          lineHeight: ["145%"],
          letterSpacing: ["2%"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([24])),
        },
        careerPrimaryCTA: {
          fontFamily: "heading",
          fontSize: [20, 16, 16],
          lineHeight: ["135%"],
          letterSpacing: ["0.02em"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([20, 16, 16])),
        },
        careerJobSubtitle: {
          fontFamily: "heading",
          fontSize: [12, 18, 18],
          lineHeight: ["132%", "100%", "100%"],
          letterSpacing: ["1.5%"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([12, 18, 18])),
        },
        careerJobCheckBoxText: {
          fontFamily: "heading",
          fontSize: [16, 20],
          lineHeight: ["125%", "110%"],
          letterSpacing: ["0.02em"],
          fontWeight: ["700"],
          ...(leadingTrim && getLeadingTrimStyle([12, 16])),
        },
        campaignLandingTitle: {
          fontFamily: "heading",
          fontSize: [41],
          lineHeight: ["120%"],
          letterSpacing: ["0.02em"],
          fontWeight: ["700"],
          ...(leadingTrim && getLeadingTrimStyle([42])),
        },
        campaignLandingFaqQuestion: {
          fontFamily: "heading",
          fontSize: [23],
          lineHeight: ["122%"],
          letterSpacing: ["0.02em"],
          fontWeight: ["700"],
          ...(leadingTrim && getLeadingTrimStyle([23])),
        },
        campaignLandingFaqAnswer: {
          fontFamily: "heading",
          fontSize: [14],
          lineHeight: ["200%"],
          letterSpacing: ["0.02em"],
          fontWeight: ["300"],
          ...(leadingTrim && getLeadingTrimStyle([14])),
        },
        appTooltipCTA: {
          fontFamily: "heading",
          fontSize: [16, 18, 20],
          lineHeight: ["100%"],
          letterSpacing: ["0.02em"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([16, 18, 20])),
        },
        appTableHeader: {
          fontFamily: "heading",
          fontSize: [14, 16, 28],
          lineHeight: ["135%", "118%", "140%"],
          fontWeight: ["700"],
          ...(leadingTrim && getLeadingTrimStyle([14, 16, 28])),
        },
        appTableBody: {
          fontFamily: "heading",
          fontSize: [14, 14, 16],
          lineHeight: ["155%"],
          fontWeight: ["400"],
          ...(leadingTrim && getLeadingTrimStyle([14, 14, 16])),
        },
        appTableTitle: {
          fontFamily: "heading",
          fontSize: [18, 18, 28],
          lineHeight: ["110%", "110%", "140%"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([18, 18, 28])),
        },
        label_medium: {
          fontFamily: "body",
          fontSize: 12,
          lineHeight: ["133%"],
          fontWeight: ["600"],
          ...(leadingTrim && getLeadingTrimStyle([12])),
        },
      },
    })}
`
const getTagFromVariant = variant => {
  switch (variant) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
      return variant
    case "h6":
    case "h7":
    case "subtitle":
      return "h5"
    case "hiddenBodyCopy":
    case "bodyCopy":
    case "bodyCopy2":
      return "div"
    default:
      return "p"
  }
}

const ForwardedTypography = forwardRef<HTMLAllCollection, TypographyProps>(
  ({ as, variant, ...props }, ref) => {
    const { i18n } = useTranslation("")
    return (
      <Typography
        as={as || getTagFromVariant(variant)}
        variant={variant}
        ref={ref}
        wordBreak={getWordBreak(i18n.language)}
        {...props}
      />
    )
  }
)

ForwardedTypography.displayName = "Typography"

export default ForwardedTypography
